/**
 * MobileIftHome — IFT mobile home at `/m`.
 *
 * Rebuilt Oct 2026 to match the user-provided design reference:
 *   • Serif brown hero "Our Families · Our Stories · Our Heritage"
 *     with a backwater-and-vintage-photos decorative illustration.
 *   • Pill search bar with green "Search" button.
 *   • 8-tile primary category grid (4×2).
 *   • Life Occasions horizontal circle scroll.
 *   • Featured Stories 2-up card pair.
 *   • Explore by Community horizontal card scroll.
 *
 * All data is static navigation — the shell handles signed-in/anon via
 * its 6th bottom-nav slot ("Sign In" vs "Me"). This home is identical
 * for both states (as per the design).
 */
import { Link } from "react-router-dom";
import {
  Search, TreePine, Users, Landmark, Globe, Church, Calendar, Flame,
  Cake, GraduationCap, Heart, Bird, Flower2, UserX, Candy, ChevronRight,
  ArrowRight,
} from "lucide-react";
import MobileIftShell from "./MobileIftShell";

/* ---------------------- palette ------------------------- */
const P = {
  cream:       "#FEF9F0",
  parchment:   "#FAF6EE",
  paper:       "#FFFFFF",
  ink:         "#3B2E1F",
  inkSoft:     "#6B5A3F",
  inkMuted:    "#9A8970",
  green:       "#15803D",
  greenDeep:   "#166534",
  saffron:     "#C2410C",
  saffronSoft: "#FFEDD5",
  gold:        "#D97706",
  goldSoft:    "#FEF3C7",
  maroon:      "#78350F",
  tileBorder:  "rgba(59,46,31,0.08)",
  cardShadow:  "0 2px 10px rgba(59,46,31,0.06)",
};

/* ---------------------- hero illustration ---------------- */
function HeroArt() {
  // Painted hero generated via Gemini Nano Banana. Composition: Kerala
  // backwater with a kettuvallam houseboat on the left; a lush banyan-
  // style ancestral tree on the right with four sepia South Indian
  // family portraits pinned to the trunk; golden-hour sunset haze.
  // Source lives in /app/frontend/public/assets/mobile-hero.jpg — can
  // be regenerated via `python -m scripts.generate_mobile_hero`.
  return (
    <div
      className="relative w-full overflow-hidden rounded-2xl"
      style={{ aspectRatio: "16 / 9", boxShadow: "0 6px 20px rgba(59,46,31,0.12)" }}
    >
      <img
        src="/assets/mobile-hero.jpg"
        alt="A banyan tree with pinned sepia family portraits beside a Kerala backwater at sunset"
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
        fetchpriority="high"
      />
    </div>
  );
}

/* ---------------------- primary tile ---------------- */
function PrimaryTile({ to, Icon, title, subtitle, color, testid, bg }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex flex-col items-center text-center py-4 px-2 rounded-2xl active:scale-[0.97] transition-transform"
      style={{
        background: P.paper,
        border: `1px solid ${P.tileBorder}`,
        boxShadow: P.cardShadow,
      }}
    >
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-2"
        style={{ background: bg || P.goldSoft, color }}
      >
        <Icon className="w-7 h-7" strokeWidth={1.8} />
      </div>
      <div className="text-[13px] font-bold leading-tight" style={{ color: P.ink }}>{title}</div>
      <div className="text-[10.5px] mt-0.5 leading-tight" style={{ color: P.inkSoft }}>{subtitle}</div>
    </Link>
  );
}

/* ---------------------- life-occasion circle ---------------- */
function LifeOccasion({ to, Icon, label, color, bg, testid, badge }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex flex-col items-center min-w-[72px] gap-1.5 flex-shrink-0 snap-start active:scale-95 transition-transform"
    >
      <div
        className="relative w-14 h-14 rounded-full flex items-center justify-center"
        style={{ background: bg, color, border: `1px solid ${color}22` }}
      >
        {badge ? (
          <span className="text-[13px] font-bold tracking-tight" style={{ color }}>{badge}</span>
        ) : (
          <Icon className="w-6 h-6" strokeWidth={1.8} />
        )}
      </div>
      <span className="text-[10.5px] font-medium text-center leading-tight" style={{ color: P.ink }}>
        {label}
      </span>
    </Link>
  );
}

/* ---------------------- community card ---------------- */
function CommunityCard({ to, label, subtitle, color, bg, Icon, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="min-w-[120px] flex-shrink-0 snap-start active:scale-[0.97] transition-transform"
    >
      <div
        className="w-full h-24 rounded-2xl flex items-center justify-center relative overflow-hidden"
        style={{ background: bg, border: `1px solid ${P.tileBorder}` }}
      >
        <Icon className="w-12 h-12" style={{ color }} strokeWidth={1.3} />
      </div>
      <div className="mt-2 px-1">
        <div className="text-[12.5px] font-bold leading-tight" style={{ color: P.ink }}>{label}</div>
        <div className="text-[10px] mt-0.5 leading-tight" style={{ color: P.inkSoft }}>{subtitle}</div>
      </div>
    </Link>
  );
}

/* ---------------------- featured story card ---------------- */
function FeaturedStoryCard({ to, title, subtitle, variant, testid }) {
  const dark = variant === "dark";
  return (
    <Link
      to={to}
      data-testid={testid}
      className="relative flex items-end p-3.5 rounded-2xl overflow-hidden min-h-[128px] active:scale-[0.98] transition-transform"
      style={{
        background: dark
          ? "linear-gradient(135deg, #3B2E1F 0%, #5C3317 60%, #8B4513 100%)"
          : "linear-gradient(135deg, #D4A574 0%, #B4845C 60%, #8B5A3C 100%)",
      }}
    >
      {/* Decorative mark */}
      <div className="absolute right-3 top-3 opacity-90">
        {dark ? (
          <div className="relative">
            <Flame className="w-10 h-10" style={{ color: "#FBBF24" }} />
            <div className="absolute -bottom-1 inset-x-0 h-2 rounded-full"
                 style={{ background: "radial-gradient(ellipse, #FBBF24 0%, transparent 70%)" }} />
          </div>
        ) : (
          <TreePine className="w-10 h-10" style={{ color: "#F5EAD3" }} />
        )}
      </div>
      <div className="relative z-10">
        <div className="text-[15px] font-bold leading-tight" style={{ color: "#FFF", fontFamily: "'Playfair Display', Georgia, serif" }}>
          {title}
        </div>
        <div className="text-[11px] mt-1 leading-snug max-w-[18ch]" style={{ color: "rgba(255,255,255,0.85)" }}>
          {subtitle}
        </div>
        <ChevronRight className="w-4 h-4 mt-1" style={{ color: "rgba(255,255,255,0.85)" }} />
      </div>
    </Link>
  );
}

/* ---------------------- section header ---------------- */
function SectionHeader({ title, to, label = "View All" }) {
  return (
    <div className="flex items-end justify-between mb-3 px-4">
      <h2 className="text-[20px] font-bold leading-tight" style={{ color: P.ink, fontFamily: "'Playfair Display', Georgia, serif" }}>
        {title}
      </h2>
      {to && (
        <Link to={to} className="text-[12px] font-semibold flex items-center gap-0.5" style={{ color: P.green }}>
          {label} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </div>
  );
}

/* ========================================================
 *  Main component
 * ====================================================== */
export default function MobileIftHome() {
  return (
    <MobileIftShell>
      <div data-testid="ift-mhome" style={{ background: P.parchment }}>

        {/* ────── Hero ────── */}
        <section className="pt-5 pb-4 px-4" data-testid="ift-mhome-hero">
          <h1
            className="text-[34px] leading-[1.02] font-bold tracking-tight"
            style={{ color: P.ink, fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Our Families<br/>Our Stories<br/>Our Heritage
          </h1>
          <p className="mt-3 text-[13px] leading-relaxed max-w-[32ch]" style={{ color: P.inkSoft }}>
            Preserve your family history, celebrate loved ones and keep memories alive for generations.
          </p>
          <div className="mt-4">
            <HeroArt />
          </div>
        </section>

        {/* ────── Search bar ────── */}
        <section className="px-4 pb-5" data-testid="ift-mhome-search">
          <form
            action="/search"
            method="get"
            className="flex items-center gap-1 rounded-full"
            style={{
              background: P.paper,
              border: `1px solid ${P.tileBorder}`,
              boxShadow: P.cardShadow,
              padding: 4,
            }}
          >
            <div className="pl-3 pr-1 flex items-center" style={{ color: P.inkMuted }}>
              <Search className="w-4 h-4" />
            </div>
            <input
              type="search"
              name="q"
              placeholder="Search for a person, family or place…"
              className="flex-1 bg-transparent outline-none text-[13px] py-2.5"
              style={{ color: P.ink }}
              data-testid="ift-mhome-search-input"
            />
            <button
              type="submit"
              className="px-5 py-2.5 rounded-full text-[13px] font-semibold text-white active:scale-95 transition-transform"
              style={{ background: P.green }}
              data-testid="ift-mhome-search-btn"
            >
              Search
            </button>
          </form>
        </section>

        {/* ────── 8-tile primary category grid (4×2) ────── */}
        <section className="px-4 pb-6" data-testid="ift-mhome-primary-grid">
          <div className="grid grid-cols-4 gap-2.5">
            <PrimaryTile
              to="/family-trees" testid="tile-family-trees"
              Icon={TreePine} title="Family Trees" subtitle="Build & Explore"
              color={P.green}   bg="#D1FAE5"
            />
            <PrimaryTile
              to="/celebrate"   testid="tile-memorials"
              Icon={Flame}      title="Memorials"    subtitle="Celebrate a Life"
              color={P.saffron} bg="#FEE4D4"
            />
            <PrimaryTile
              to="/genealogy"   testid="tile-genealogy"
              Icon={Users}      title="Genealogy"    subtitle="Discover Your Roots"
              color="#2563EB"   bg="#DBEAFE"
            />
            <PrimaryTile
              to="/heritage"    testid="tile-heritage"
              Icon={Landmark}   title="Heritage"     subtitle="People, Places"
              color={P.gold}    bg={P.goldSoft}
            />
            <PrimaryTile
              to="/cemeteries"  testid="tile-cemeteries"
              Icon={Flower2}    title="Cemeteries"   subtitle="Find & Remember"
              color={P.inkSoft} bg="#E5E5E5"
            />
            <PrimaryTile
              to="/life-occasions" testid="tile-life-occasions"
              Icon={Calendar}      title="Life Events"  subtitle="Birthdays & More"
              color={P.saffron}    bg="#FEE4D4"
            />
            <PrimaryTile
              to="/find-a-relative" testid="tile-find-relative"
              Icon={Search}         title="Find Relative" subtitle="Reconnect"
              color={P.green}       bg="#D1FAE5"
            />
            <PrimaryTile
              to="/communities"   testid="tile-global"
              Icon={Globe}        title="Indian & Global" subtitle="All Communities"
              color="#1D4ED8"     bg="#DBEAFE"
            />
          </div>
        </section>

        {/* ────── Life Occasions ────── */}
        <section className="pb-6" data-testid="ift-mhome-life-occasions">
          <SectionHeader title="Life Occasions" to="/life-occasions" />
          <div
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory px-4 pb-1"
            style={{ scrollbarWidth: "none" }}
          >
            <style>{`[data-testid="ift-mhome-life-occasions"] ::-webkit-scrollbar{display:none}`}</style>
            <LifeOccasion to="/life-occasions/birthday"            testid="lo-birthday"
                          Icon={Cake}         label="Birthday"
                          color={P.saffron}   bg="#FEE4D4"/>
            <LifeOccasion to="/life-occasions/graduation"          testid="lo-grad"
                          Icon={GraduationCap} label="Graduation"
                          color={P.ink}       bg="#EDE4D3"/>
            <LifeOccasion to="/life-occasions/wedding-anniversary" testid="lo-anniv"
                          Icon={Heart}        label="Wedding Anniversary"
                          color={P.gold}      bg={P.goldSoft}/>
            <LifeOccasion to="/life-occasions/milestone"           testid="lo-milestone"
                          badge="50"          label={"50th / 60th\n70th / 80th / 90th"}
                          color={P.gold}      bg={P.goldSoft}/>
            <LifeOccasion to="/life-occasions/obituary"            testid="lo-obituary"
                          Icon={Bird}         label="Obituary"
                          color="#2563EB"     bg="#DBEAFE"/>
            <LifeOccasion to="/life-occasions/memorial"            testid="lo-memorial"
                          Icon={Flame}        label="Memorial"
                          color={P.saffron}   bg="#FEE4D4"/>
            <LifeOccasion to="/life-occasions/missing"             testid="lo-missing"
                          Icon={UserX}        label={"Missing\nPeople"}
                          color="#7C3AED"     bg="#EDE9FE"/>
          </div>
        </section>

        {/* ────── Featured Stories ────── */}
        <section className="pb-6" data-testid="ift-mhome-featured">
          <SectionHeader title="Featured Stories" to="/articles" label="See All" />
          <div className="px-4 grid grid-cols-2 gap-3">
            <FeaturedStoryCard
              to="/celebrate" testid="feat-memorials" variant="dark"
              title="Loving Memorials"
              subtitle="Honor and remember your loved ones."
            />
            <FeaturedStoryCard
              to="/family-trees" testid="feat-trees" variant="light"
              title="Family Trees"
              subtitle="Build, explore and share your family history."
            />
          </div>
        </section>

        {/* ────── Explore by Community ────── */}
        <section className="pb-8" data-testid="ift-mhome-communities">
          <SectionHeader title="Explore by Community" to="/communities" />
          <div
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory px-4 pb-1"
            style={{ scrollbarWidth: "none" }}
          >
            <style>{`[data-testid="ift-mhome-communities"] ::-webkit-scrollbar{display:none}`}</style>
            <CommunityCard
              to="/communities/hindu" testid="com-hindu"
              label="Hindu" subtitle="Traditions & Heritage"
              Icon={Landmark} color="#C2410C" bg="#FEE4D4"
            />
            <CommunityCard
              to="/communities/kerala-christian" testid="com-kerala-christian"
              label="Kerala Christian" subtitle="Families & Parishes"
              Icon={Church} color="#1D4ED8" bg="#DBEAFE"
            />
            <CommunityCard
              to="/communities/muslim" testid="com-muslim"
              label="Muslim" subtitle="Communities & History"
              Icon={Landmark} color={P.green} bg="#D1FAE5"
            />
            <CommunityCard
              to="/communities/sikh" testid="com-sikh"
              label="Sikh" subtitle="Heritage & Stories"
              Icon={Landmark} color={P.gold} bg={P.goldSoft}
            />
            <CommunityCard
              to="/communities" testid="com-global"
              label="Global" subtitle="All Communities"
              Icon={Globe} color="#1D4ED8" bg="#DBEAFE"
            />
          </div>
        </section>

        {/* Bottom spacer so content clears the fixed nav */}
        <div style={{ height: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }} />
      </div>
    </MobileIftShell>
  );
}
